import React, { useCallback, useEffect, useState } from 'react';
import BioDataAssistant from './BioDataAssistant';
import { useLocation } from 'react-router-dom';
import { SUPPORT_ROUTES } from './assistant-policy.mjs';
import { withAssistantDeadline } from './assistant-deadline.mjs';
import { getSessionToken, clearSessionToken, useSessionToken } from '../../auth/session';

// Copy into frontend/src/components/assistant beside BioDataAssistant.
// openAccount is the registered existing LandingPage account-modal callback.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export default function BioDataAssistantHost({ openAccount, enabled = false }) {
  const token = useSessionToken();
  const { pathname } = useLocation();
  const [sessionRevision, setSessionRevision] = useState(0);
  const [recheck, setRecheck] = useState(0);
  const [verified, setVerified] = useState({ token: null, revision: -1, status: 'loading' });
  useEffect(() => {
    // Cookie-only logout must invalidate the guide even if localStorage remains null.
    const changed = () => setSessionRevision(previous => previous + 1);
    const storage = event => { if (event.key === 'session_token' || event.key === null) changed(); };
    window.addEventListener('bds-session-changed', changed);
    window.addEventListener('storage', storage);
    const focus = () => setRecheck(previous => previous + 1);
    window.addEventListener('focus', focus);
    return () => { window.removeEventListener('bds-session-changed', changed); window.removeEventListener('storage', storage); window.removeEventListener('focus', focus); };
  }, []);
  useEffect(() => {
    if (!enabled) { setVerified({ token, revision: sessionRevision, status: 'unavailable' }); return; }
    const controller = new AbortController();
    let active = true;
    setVerified(previous => previous.token === token && previous.revision === sessionRevision && previous.status !== 'unavailable' ? previous : { token, revision: sessionRevision, status: 'loading' });
    async function verify() {
      try {
        const result = await withAssistantDeadline(async () => {
        const response = await fetch(`${API}/auth/me`, { credentials: 'include', cache: 'no-store', signal: controller.signal, headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (controller.signal.aborted || !active || getSessionToken() !== token) return null;
        if (response.status === 401) {
          return { status: 'anonymous' };
        }
        if (!response.ok) throw new Error('Cannot verify session');
        const user = await response.json();
        if (!user?.user_id) throw new Error('Verified identity unavailable');
        return { status: 'authenticated', identityKey: user.user_id };
        }, controller);
        // Account changes can occur while reading the response body.
        if (!active || controller.signal.aborted || getSessionToken() !== token || !result) return;
        if (result.status === 'anonymous' && token) clearSessionToken();
        else setVerified({ token, revision: sessionRevision, ...result });
      } catch {
        // Network/server failures do not turn an existing login into a prospect.
        if (active && getSessionToken() === token) setVerified({ token, revision: sessionRevision, status: 'unavailable' });
      }
    }
    void verify();
    return () => { active = false; controller.abort(); };
  }, [token, enabled, sessionRevision, recheck]);
  const requestSession = useCallback(async (body, { signal } = {}) => {
    const current = getSessionToken();
    const response = await fetch(`${API}/assistant/session`, {
      method: 'POST', credentials: 'include', cache: 'no-store', signal,
      headers: { 'Content-Type': 'application/json', ...(current ? { Authorization: `Bearer ${current}` } : {}) },
      body: JSON.stringify(body)
    });
    if (getSessionToken() !== current) throw new Error('Session changed');
    if (response.status === 401) { clearSessionToken(); throw new Error('Session expired'); }
    if (!response.ok) throw new Error('Assistant unavailable');
    return response.json();
  }, []);
  const status = verified.token === token && verified.revision === sessionRevision ? verified.status : 'loading';
  // Keep the lifecycle owner mounted when OFF so re-enabling cannot bypass teardown.
  return <>
    <BioDataAssistant identityKey={verified.identityKey ?? null} authStatus={enabled ? status : 'unavailable'} requestSession={requestSession} openAccount={openAccount} />
    {enabled && status === 'unavailable' && (pathname === '/' || SUPPORT_ROUTES.test(pathname)) &&
      <aside className="bds-assistant" aria-label="Assistant unavailable"><button className="bds-assistant-launcher" onClick={() => setRecheck(previous => previous + 1)}>Retry help assistant</button></aside>}
  </>;
}
